import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  FormHelperText,
  FormLabel,
  Group,
  IconExternalLink,
  IconImage,
  Input,
  Loader,
  Typography,
  Wrapper
} from '@screentone/core';

import { HelpIcon } from 'components/help-icon/HelpIcon';
import { SnippetyResponse, useSnippetyQuery } from 'hooks/useSnippetyQuery';
import { IMAGE_MANAGER_DOMAIN } from 'utils/constants';
import { isValidURL } from 'utils/url';
import styles from './AddIframeModal.module.scss';

const PUBLICATION_FALLBACK_IMAGE: Record<string, string> = {
  wsj: 'https://s.wsj.net/img/meta/wsj-social-share.png',
  barrons: 'https://s.barrons.com/img/meta/barrons-social-share.png',
  marketwatch: 'https://mw3.wsj.net/mw5/content/logos/mw_logo_social.png'
};

export interface AddIframeData {
  iframeUrl: string;
  fallbackImageUrl: string;
  fallbackLinkUrl: string;
  fallbackLinkText: string;
}

interface AddIframeModalProps {
  onAdd: (data: AddIframeData) => void;
  onCancel: () => void;
  publicationKey?: string;
  initialData?: AddIframeData;
}

export const AddIframeModal = ({ onAdd, onCancel, publicationKey, initialData }: AddIframeModalProps) => {
  const defaultFallbackImageUrl = (publicationKey && PUBLICATION_FALLBACK_IMAGE[publicationKey]) ?? '';
  const imageManagerUrl = publicationKey ? `${IMAGE_MANAGER_DOMAIN}/${publicationKey}/` : undefined;

  const [iframeUrl, setIframeUrl] = useState(initialData?.iframeUrl ?? '');
  const [fallbackImageUrl, setFallbackImageUrl] = useState(initialData?.fallbackImageUrl ?? '');
  const [fallbackLinkUrl, setFallbackLinkUrl] = useState(initialData?.fallbackLinkUrl ?? '');
  const [fallbackLinkText, setFallbackLinkText] = useState(initialData?.fallbackLinkText ?? '');

  // When editing an existing iframe (initialData provided), don't let Snippety overwrite the
  // existing fallback values on first render. Only allow Snippety to auto-populate after the
  // user explicitly changes the iframe URL.
  const hasUserEditedUrlRef = useRef(!initialData?.iframeUrl);

  const [hasUrlError, setHasUrlError] = useState(false);
  const [hasFallbackImageError, setHasFallbackImageError] = useState(false);
  const [hasFallbackLinkError, setHasFallbackLinkError] = useState(false);

  // Only call Snippety when the URL is non-empty and valid to avoid unnecessary API calls
  const snippetyUrl = iframeUrl && !hasUrlError ? iframeUrl : '';
  const { data: snippetyData, isLoading: isSnippetyLoading } = useSnippetyQuery(snippetyUrl);
  const snippetyResponse = snippetyData as SnippetyResponse | null;
  const previewImageUrl = snippetyResponse?.media ?? '';

  // When Snippety responds, auto-populate fallback fields: link URL on success, image URL if `media`
  // is present; on error, clear link URL since it would be an invalid destination
  useEffect(() => {
    if (!snippetyUrl) return;
    if (!snippetyData) return;
    if (!hasUserEditedUrlRef.current) return;

    const hasError = (snippetyResponse as unknown as { error?: boolean }).error === true;

    if (hasError) {
      setFallbackLinkUrl('');
    } else {
      setFallbackLinkUrl(iframeUrl);
      if (snippetyResponse?.media) {
        setFallbackImageUrl(snippetyResponse.media);
      } else {
        setHasFallbackImageError(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snippetyData, snippetyUrl]);

  const handleIframeUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    hasUserEditedUrlRef.current = true;
    const trimmed = e.target.value.trim();
    setIframeUrl(trimmed);
    const urlValid = isValidURL(trimmed);
    setHasUrlError(!urlValid);
    // Clear fallback image so a stale preview from a previous URL doesn't persist
    setFallbackImageUrl('');
    setHasFallbackImageError(false);
    // A newly-valid URL will auto-populate the link URL via Snippety, so clear any stale link error
    if (urlValid) setHasFallbackLinkError(false);
  };

  // Block submission while Snippety is loading so the user can't add before fallback fields are populated
  const isAddDisabled =
    !iframeUrl.trim() ||
    hasUrlError ||
    isSnippetyLoading ||
    !fallbackImageUrl.trim() ||
    hasFallbackImageError ||
    hasFallbackLinkError;

  return (
    <Box data-testid="add-iframe-modal" className={styles.modalBox}>
      <Box.Title data-testid="add-iframe-modal-title">Add iFrame</Box.Title>
      <Box.Content padding={{ all: 'none' }}>
        <Wrapper padding={{ all: 'mlg' }}>
          {/* iFrame URL row: preview box on left, label+input on right */}
          <Group gap="md" align="start" margin={{ bottom: 'md' }}>
            <div className={styles.iframePreviewBox} data-testid="add-iframe-preview-box">
              {isSnippetyLoading ? (
                <div className={styles.iframePreviewPlaceholder}>
                  <Loader size="md" />
                </div>
              ) : previewImageUrl ? (
                <div
                  className={styles.iframePreviewFrame}
                  data-testid="add-iframe-preview-frame"
                  style={{ backgroundImage: `url(${previewImageUrl})` }}
                />
              ) : (
                <div className={styles.iframePreviewPlaceholder} data-testid="add-iframe-preview-placeholder">
                  <IconImage size="lg" color="asphalt" />
                </div>
              )}
            </div>
            <div className={styles.iframeUrlSection}>
              <FormLabel label="iFrame URL" labelPosition="top" fullWidth>
                <Input
                  data-testid="add-iframe-url-input"
                  value={iframeUrl}
                  error={hasUrlError}
                  onChange={handleIframeUrlChange}
                  // Re-validate on blur — Screentone clears error styling on blur without this
                  onBlur={() => setHasUrlError(!isValidURL(iframeUrl))}
                  placeholder="Enter iFrame URL here..."
                  className={styles.iframeUrlInput}
                />
              </FormLabel>
              {hasUrlError && (
                <FormHelperText error data-testid="add-iframe-url-error">
                  Invalid URL
                </FormHelperText>
              )}
            </div>
          </Group>

          <Divider />

          {/* iFrame Fallbacks section */}
          <Wrapper margin={{ vertical: 'sm' }}>
            <Group gap="xs" valign="end">
              <Typography variant="label3">iFrame Fallbacks</Typography>
              <HelpIcon isLeftAnchored>
                <Typography variant="label3">iFrame Fallback</Typography>
                <Typography variant="note">
                  When adding an iFrame, it is important to also add a fallback image to display in cases where iFrame
                  isn&apos;t supported to ensure a good customer experience across platforms.
                </Typography>
                <br />
                <Typography variant="label3">Fallback Image URL</Typography>
                <Typography variant="note">
                  Add the link to your fallback image here. If you don&apos;t have a fallback and for some reason need
                  to publish immediately, the Placeholder button will add an image of your brand&apos;s logo for a
                  fallback.
                </Typography>
                <br />
                <Typography variant="label3">Fallback Link URL</Typography>
                <Typography variant="note">
                  Add a link here to where you&apos;d like the user to be linked to when they click on the fallback
                  image.
                </Typography>
                <br />
                <Typography variant="label3">Fallback Link Text</Typography>
                <Typography variant="note">This is what the fallback link will display as.</Typography>
              </HelpIcon>
            </Group>
            <Typography size="sm" color="asphalt">
              Add an image placeholder or search Image Manager In case the iFrame is not supported. Ex: Apple News.
            </Typography>
          </Wrapper>

          {/* Fallback Image URL */}
          <Wrapper margin={{ bottom: 'xs' }}>
            <Group gap="sm" align="center">
              {fallbackImageUrl ? (
                <div
                  className={styles.fallbackImagePreview}
                  data-testid="add-iframe-fallback-image-preview"
                  style={{ backgroundImage: `url(${fallbackImageUrl})` }}
                />
              ) : (
                <div className={styles.fallbackImageEmptyPreview} data-testid="add-iframe-fallback-image-placeholder">
                  <IconImage size="lg" color="asphalt" />
                </div>
              )}
              <div className={styles.fallbackImageInput}>
                <FormLabel label="Fallback Image URL" labelPosition="top" required fullWidth>
                  <Input
                    data-testid="add-iframe-fallback-image-url-input"
                    value={fallbackImageUrl}
                    error={hasFallbackImageError}
                    placeholder="Adding a fallback image is required."
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      setFallbackImageUrl(e.target.value);
                    }}
                    onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                      const v = e.target.value.trim();
                      setHasFallbackImageError(v.length > 0 && !isValidURL(v));
                      // Re-run iFrame URL validation to keep its error visible after the user tabs away
                      setHasUrlError(!isValidURL(iframeUrl));
                    }}
                  />
                </FormLabel>
                <FormHelperText error={hasFallbackImageError} data-testid="add-iframe-fallback-image-error">
                  {hasFallbackImageError ? 'The provided image url is not supported' : ' '}
                </FormHelperText>
              </div>
            </Group>
          </Wrapper>

          {/* Action links below fallback image */}
          <Group gap="md" margin={{ bottom: 'md' }}>
            <Button
              tertiary
              data-testid="add-iframe-placeholder-image-button"
              onClick={() => {
                setFallbackImageUrl(defaultFallbackImageUrl);
                // Verify the placeholder URL actually loads before clearing the error —
                // guards against a missing or broken image asset
                const img = new Image();
                img.onload = () => setHasFallbackImageError(false);
                img.onerror = () => setHasFallbackImageError(true);
                img.src = defaultFallbackImageUrl;
              }}
            >
              + Placeholder Image
            </Button>
            <Button
              tertiary
              data-testid="add-iframe-open-image-manager-button"
              icon={IconExternalLink}
              iconPosition="right"
              disabled={!imageManagerUrl}
              onClick={() => window.open(imageManagerUrl, '_blank')}
            >
              Open Image Manager
            </Button>
          </Group>

          {/* Fallback Link URL */}
          <Wrapper margin={{ bottom: 'md' }}>
            <FormLabel label="Fallback Link URL" labelPosition="top" fullWidth>
              <Input
                data-testid="add-iframe-fallback-link-url-input"
                value={fallbackLinkUrl}
                error={hasFallbackLinkError}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  setFallbackLinkUrl(e.target.value);
                }}
                onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                  const v = e.target.value.trim();
                  if (!v) {
                    // If the user clears the field, restore it to the iFrame URL
                    setFallbackLinkUrl(iframeUrl);
                    setHasFallbackLinkError(false);
                  } else {
                    setHasFallbackLinkError(!isValidURL(v));
                  }
                  // Re-run iFrame URL validation to keep its error visible after the user tabs away
                  setHasUrlError(!isValidURL(iframeUrl));
                }}
              />
            </FormLabel>
            {hasFallbackLinkError && (
              <FormHelperText error data-testid="add-iframe-fallback-link-error">
                The provided link url is not supported
              </FormHelperText>
            )}
          </Wrapper>

          {/* Fallback Link Text */}
          <Wrapper>
            <FormLabel label="Fallback Link Text" labelPosition="top" fullWidth>
              <Input
                data-testid="add-iframe-fallback-link-text-input"
                value={fallbackLinkText}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFallbackLinkText(e.target.value)}
                // Re-run iFrame URL validation to keep its error visible after the user tabs away
                onBlur={() => setHasUrlError(!isValidURL(iframeUrl))}
              />
            </FormLabel>
          </Wrapper>
        </Wrapper>

        <Wrapper padding={{ top: 'sm', right: 'mlg', bottom: 'md', left: 'mlg' }}>
          <Group gap="sm" align="end" className={styles.footerButtonsWrapper}>
            <Button data-testid="add-iframe-cancel-button" secondary onClick={onCancel}>
              Cancel
            </Button>
            <Button
              data-testid="add-iframe-add-button"
              primary
              disabled={isAddDisabled}
              onClick={() => onAdd({ iframeUrl, fallbackImageUrl, fallbackLinkUrl, fallbackLinkText })}
            >
              Add
            </Button>
          </Group>
        </Wrapper>
      </Box.Content>
    </Box>
  );
};
